// nmn-quiz-results-page.js

/* RESULTS - OPTIMIZED VERSION */

// -----------------------------------------------------
// Shopify Storefront API CONFIG (safe to expose)
// -----------------------------------------------------
const SHOP_DOMAIN = "tailormade-health.myshopify.com";
const STOREFRONT_TOKEN = "7d8fb1f19b8eaf27a7a574412283e285";

// Units shipped per delivery. Recharge publishes this as a selling plan option; a plan that ships
// one unit reports 0 there, and older plan groups omit the option, so anything that is not a
// number above 1 means one unit.
function getPlanQuantity(plan) {
  var option = (plan?.options || []).find(function (entry) { return entry.name === 'Product Quantity'; });
  var parsed = parseInt(option?.value, 10);
  return isNaN(parsed) || parsed < 1 ? 1 : parsed;
}

// Prices beside a plan are for the whole delivery, so a multi-unit plan has to name the count.
function getPlanLabel(plan) {
  if (!plan) return '';
  return plan.quantity > 1 ? plan.name + ' (' + plan.quantity + ' units)' : plan.name;
}

// Every figure on the card is tied to the chosen plan — its per-delivery price, how many units
// that delivery holds, and its discount — so all of them are rewritten when the plan changes.
// Rendering them once from sellingPlans[0] was invisible while every plan cost the same.
function applySelectedPlanDisplay(productContainer, plan) {
  if (!productContainer || !plan) return;

  var quantity = plan.quantity || 1;
  var total = (plan.perDeliveryPrice * quantity).toFixed(2);
  var wasTotal = ((plan.compareAt || plan.perDeliveryPrice) * quantity).toFixed(2);

  var mainPrice = productContainer.querySelector('.qp-price-info--first .qp-product-price');
  if (mainPrice) mainPrice.textContent = '$' + total;

  var mainWas = productContainer.querySelector('.qp-price-info--first .qp-old-price');
  if (mainWas) mainWas.textContent = '$' + wasTotal;

  var subPrice = productContainer.querySelector('.qp-subscription .qp-current-price');
  if (subPrice) subPrice.textContent = '$' + total;

  var subWas = productContainer.querySelector('.qp-subscription .qp-old-price');
  if (subWas) subWas.textContent = '$' + wasTotal;

  var save = productContainer.querySelector('.qp-suscription-save');
  if (save) {
    save.innerHTML = plan.discountPercent
      ? '<span class="qp-save-extra">Save ' + plan.discountPercent + '%</span>'
      : '';
  }
}

// The headline price follows the purchase type. One-time is always a single unit of the product
// at list price, so it must not inherit the subscription tier's units.
function applyPurchaseTypeDisplay(productContainer, isSubscription) {
  if (!productContainer) return;

  var mainPrice = productContainer.querySelector('.qp-price-info--first .qp-product-price');
  var mainWas = productContainer.querySelector('.qp-price-info--first .qp-old-price');
  var oneTimeEl = productContainer.querySelector('.qp-one-time .qp-current-price');

  if (isSubscription) return;

  if (mainPrice && oneTimeEl) mainPrice.textContent = oneTimeEl.textContent;
  if (mainWas) mainWas.textContent = '';
  // The savings badge belongs to the subscription block and keeps advertising that tier's
  // discount. Emptying it here left a styled pill with no text in it.
}

/**************************************
 * GRAPHQL FETCH WRAPPER
 **************************************/
async function storefrontQuery(query, variables = {}) {
  const res = await fetch(`https://${SHOP_DOMAIN}/api/2024-01/graphql.json`, {
    method: "POST",
    headers: {
      "Content-Type": "application/json",
      "X-Shopify-Storefront-Access-Token": STOREFRONT_TOKEN
    },
    body: JSON.stringify({ query, variables })
  });

  const json = await res.json();

  return json.data;
}

/**************************************
 * COMBINED PRODUCT DATA FETCH (OPTIMIZED)
 **************************************/
async function fetchAllProductData(productId, productHandle) {
  const gid = `gid://shopify/Product/${productId}`;
  
  const query = `
    query ProductData($id: ID!, $handle: String!) {
      product(id: $id) {
        reviewCount: metafield(namespace: "app--1576377--reviews", key: "review_count") {
          value
        }
        productLabel: metafield(namespace: "custom", key: "product_label") {
          reference {
            ... on MediaImage {
              image {
                url
                altText
              }
            }
          }
        }

        productRationale: metafield(namespace: "custom", key: "recommendation_rationale") {
          reference {
            ... on Metaobject {
              fields {
                key
                value
              }
            }
          }
        }

        nmnImage: metafield(namespace: "custom", key: "nmn_image") {
          reference {
            ... on MediaImage {
              image {
                url
                altText
              }
            }
          }
        }

        cepImage: metafield(namespace: "custom", key: "cep_image") {
          reference {
            ... on MediaImage {
              image {
                url
                altText
              }
            }
          }
        }
        suppcoSlug: metafield(namespace: "custom", key: "suppco_slug") {
          value
        }
        suppcoShow: metafield(namespace: "custom", key: "suppco_show") {
          value
        }
      }

      productByHandle: product(handle: $handle) {
        variants(first: 1) {
          edges {
            node {
              id
              priceV2 { amount, currencyCode }
              compareAtPriceV2 { amount, currencyCode }
              sellingPlanAllocations(first: 5) {
                edges {
                  node {
                    priceAdjustments {
                      price { amount, currencyCode }
                      compareAtPrice { amount, currencyCode }
                      perDeliveryPrice { amount, currencyCode }
                    }
                    sellingPlan {
                      id
                      name
                      description
                      options { name value }
                      recurringDeliveries
                      billingPolicy {
                        ... on SellingPlanRecurringBillingPolicy {
                          interval
                          intervalCount
                        }
                      }
                      deliveryPolicy {
                        ... on SellingPlanRecurringDeliveryPolicy {
                          interval
                          intervalCount
                        }
                      }
                    }
                  }
                }
              }
            }
          }
        }
      }
    }
  `;

  const data = await storefrontQuery(query, { id: gid, handle: productHandle });
  
  const reviewCount = parseInt(data?.product?.reviewCount?.value || 0, 10);
  const reviews = { count: reviewCount, rating: 5 };
  
  const variantNode = data?.productByHandle?.variants?.edges?.[0]?.node;
  const edges = variantNode?.sellingPlanAllocations?.edges || [];
  
  // Sort selling plan options by delivery interval so the UI renders
  // in ascending order (e.g. 30, 60, 90 days).
  function getSellingPlanDays(plan) {
    const policy = plan?.deliveryPolicy || plan?.billingPolicy;

    // Prefer Shopify policy fields when available.
    if (policy?.interval && policy?.intervalCount != null) {
      const intervalCountNum = Number(policy.intervalCount);
      if (!Number.isNaN(intervalCountNum)) {
        if (policy.interval === 'DAY') return intervalCountNum;
        if (policy.interval === 'WEEK') return intervalCountNum * 7;
        if (policy.interval === 'MONTH') return intervalCountNum * 30;
      }
    }

    // Fallback: extract from plan name/description text.
    const text = `${plan?.name || ''} ${plan?.description || ''}`.toLowerCase();
    const m = text.match(/(\d+)\s*[-\s]*day[s]?/i);
    if (m) return Number(m[1]);

    // Last resort: keep unknown plans at the end.
    return Number.POSITIVE_INFINITY;
  }

  const sellingPlans = edges.map((e) => {
    const adj = e.node.priceAdjustments?.[0];
    const price = parseFloat(adj?.price?.amount || variantNode.priceV2.amount);
    const compareAt = parseFloat(adj?.compareAtPrice?.amount || price);
    const discountPercent = compareAt > price ? Math.round(((compareAt - price) / compareAt) * 100) : 0;

    return {
      id: e.node.sellingPlan.id,
      name: e.node.sellingPlan.name,
      description: e.node.sellingPlan.description,
      recurringDeliveries: e.node.sellingPlan.recurringDeliveries,
      billingPolicy: e.node.sellingPlan.billingPolicy,
      deliveryPolicy: e.node.sellingPlan.deliveryPolicy,
      discountPercent,
      price,
      compareAt,
      perDeliveryPrice: parseFloat(adj?.perDeliveryPrice?.amount || price),
      quantity: getPlanQuantity(e.node.sellingPlan),
      days: getSellingPlanDays(e.node.sellingPlan)
    };
  });
  
  const sellingPlansSorted = sellingPlans
    .map((plan, idx) => ({ plan, idx }))
    .sort((a, b) => (a.plan.days - b.plan.days) || (a.idx - b.idx))
    .map((x) => x.plan);

  const metafields = {
    productLabel: data?.product?.productLabel?.reference?.image || null,
    productRationale: data?.product?.productRationale?.reference?.fields || null,
    nmnImage: data?.product?.nmnImage?.reference?.image || null,
    cepImage: data?.product?.cepImage?.reference?.image || null,
    suppcoSlug: data?.product?.suppcoSlug?.value || null,
    suppcoShow: data?.product?.suppcoShow?.value || null
  };

  const variantPrice = parseFloat(variantNode?.priceV2?.amount || 0);
  const variantCompareAtPrice = parseFloat(variantNode?.compareAtPriceV2?.amount || variantPrice);
  
  return { reviews, sellingPlans: sellingPlansSorted, metafields, variantPrice, variantCompareAtPrice };
}

/**************************************
 * EXTRACT FIRST NAME FROM ANSWERS
 **************************************/
function extractFirstName(answers) {
  if (!answers) return '';
  
  let firstName = '';
  
  firstName = answers['first_name'] ||
              answers['First name'] ||
              '';
  
  if (!firstName && answers.smart_properties_outputs) {
    firstName = answers.smart_properties_outputs.first_name || '';
  }
  
  if (!firstName) {
    for (const key in answers) {
      if (typeof answers[key] === 'string' && 
          key.toLowerCase().includes('first') && 
          key.toLowerCase().includes('name')) {
        firstName = answers[key];
        break;
      }
    }
  }
  
  return firstName.trim();
}

/**************************************
 * CALCULATE DISCOUNT PERCENTAGE
 **************************************/
function calculateDiscount(email, phone) {
  const hasEmail = email && email.trim() !== '';
  const hasPhone = phone && phone.trim() !== '';
  
  if (hasEmail && hasPhone) {
    return { percent: 25, code: 'QUIZ25', show: true };
  }
  
  if (hasEmail) {
    return { percent: 15, code: 'QUIZ15', show: true };
  }

  return { percent: 0, code: '', show: false };
}

/**************************************
 * READ A NUMERIC QUIZ SCORE / SMART PROPERTY
 * Octane exposes computed scores as FLAT dot-prefixed keys on answers, e.g.
 * answers["points.accelerated_cellular"]. We also tolerate a nested
 * answers.points.{key} object, top-level keys, and smart_properties_outputs.
 * Coerces to a number; returns 0 when missing.
 **************************************/
function getScore(answers, key) {
  if (!answers) return 0;
  let v;
  if (answers['points.' + key] != null) {
    v = answers['points.' + key];                       // flat dotted key (Octane's format)
  } else if (answers.points && typeof answers.points === 'object' && answers.points[key] != null) {
    v = answers.points[key];                            // nested points object (fallback)
  } else if (answers[key] != null) {
    v = answers[key];                                   // top-level
  } else if (answers.smart_properties_outputs) {
    v = answers.smart_properties_outputs[key];          // smart properties bucket
  }
  const n = parseFloat(Array.isArray(v) ? v[0] : v);
  return Number.isNaN(n) ? 0 : n;
}

/**************************************
 * WAIT FOR QUIZ RESULTS CONTAINER
 **************************************/
function waitForResultsContainer() {
  return new Promise((resolve) => {
    const check = () => {
      const el = document.querySelector("#octane-quiz-outer-wrapper.oct-quiz-wrapper--result-page #octane-quiz-wrapper");
      if (el) {
        return resolve(el);
      }
      requestAnimationFrame(check);
    };
    check();
  });
}

/**************************************
 * GET HANDLE FROM URL
 **************************************/
function getHandleFromUrl(url) {
  if (!url) return null;
  const match = url.match(/\/products\/([a-zA-Z0-9\-_]+)/);
  return match ? match[1] : null;
}

/**************************************
 * GET THE QUIZ ID CONFIGURED ON THIS PAGE
 **************************************/
// Read from the Octane app block's rendered markup so swapping the quiz in the theme
// editor moves the whole page with it. Returns null if the block isn't found, which
// leaves the quiz-specific extras switched off rather than breaking the render.
function getPageQuizId() {
  return document.querySelector('.octane-ai-quiz')?.dataset.quizId || null;
}

/**************************************
 * FORMAT NUMBER WITH COMMAS
 **************************************/
function formatNumberWithCommas(number) {
  return number.toString().replace(/\B(?=(\d{3})+(?!\d))/g, ",");
}

/**************************************
 * PARSE RICH TEXT JSON TO HTML
 **************************************/
function parseRichTextToHTML(jsonString) {
  if (!jsonString) return '';
  
  try {
    const richText = typeof jsonString === 'string' ? JSON.parse(jsonString) : jsonString;
    
    function processNode(node) {
      if (!node) return '';
      
      if (node.type === 'text') {
        let text = node.value || '';
        if (node.bold) text = `<strong>${text}</strong>`;
        if (node.italic) text = `<em>${text}</em>`;
        return text;
      }
      
      const children = node.children ? node.children.map(processNode).join('') : '';
      
      switch (node.type) {
        case 'root': return children;
        case 'paragraph': return `<p>${children}</p>`;
        case 'heading':
          const level = node.level || 2;
          return `<h${level}>${children}</h${level}>`;
        case 'list':
          const listTag = node.listType === 'ordered' ? 'ol' : 'ul';
          return `<${listTag}>${children}</${listTag}>`;
        case 'list-item': return `<li>${children}</li>`;
        case 'link':
          return `<a href="${node.url}" target="${node.target || '_self'}">${children}</a>`;
        default: return children;
      }
    }
    
    return processNode(richText);
  } catch (error) {
    return jsonString;
  }
}

/**************************************
 * EXTRACT RECOMMENDATION RATIONALE FROM METAFIELD
 **************************************/
function extractRecommendationRationale(productRationaleFields) {
  if (!productRationaleFields || !Array.isArray(productRationaleFields)) {
    return null;
  }
  
  const fieldsObj = {};
  productRationaleFields.forEach(field => {
    if (field && field.key && field.value !== undefined) {
      fieldsObj[field.key] = field.value;
    }
  });
  
  const title = fieldsObj.title || 
                fieldsObj.rationale_title || 
                fieldsObj.heading ||
                'Recommendation Rationale';
  
  let description = fieldsObj.description || 
                   fieldsObj.rationale_description || 
                   fieldsObj.content ||
                   fieldsObj.text ||
                   null;

  if (!description) {
    return null;
  }
  
  if (typeof description === 'string') {
    const trimmed = description.trim();
    if ((trimmed.startsWith('{') && trimmed.endsWith('}')) || 
        (trimmed.startsWith('[') && trimmed.endsWith(']'))) {
      try {
        description = parseRichTextToHTML(description);
      } catch (error) {}
    }
  } else if (typeof description === 'object') {
    description = parseRichTextToHTML(description);
  }
  
  return { title, description };
}

/**************************************
 * MAP QUIZ ANSWER -> METAOBJECT HANDLE (SWITCH-CASE)
 **************************************/
function getQuizRationaleHandleFromAnswer(answerValue, prefix) {
  if (!answerValue) return null;
  prefix = prefix || 'nmn'; // primary product prefix: 'nmn' or 'cep'

  let value = '';
  if (Array.isArray(answerValue) && answerValue.length > 0) {
    value = String(answerValue[0]);
  } else if (typeof answerValue === 'string') {
    value = answerValue;
  } else {
    return null;
  }
  const normalized = value.trim();

  let slug;
  switch (normalized) {
    case 'Cardiovascular':
      slug = 'cardioprime'; break;
    case 'Metabolic/Blood Sugar':
      slug = 'control'; break;
    case 'Sleep':
      slug = 'ignight'; break;
    case 'Digestive':
      slug = 'giprime'; break;
    case 'Visible Aging (Hair/Skin)':
      slug = 'ghk-cu'; break;
    default:
      return null;
  }
  // e.g. nmn-cardioprime / cep-cardioprime, nmn-ghk-cu / cep-ghk-cu
  return prefix + '-' + slug;
}

/**************************************
 * FETCH QUIZ RATIONALE METAOBJECTS
 **************************************/
// One request per product: the query returns every quiz_rationale reference, so all
// handle lookups resolve against this result rather than re-querying per handle.
async function fetchQuizRationaleMetaobjects(productId) {
  if (!productId) return [];

  const gid = `gid://shopify/Product/${productId}`;

  const query = `
    query QuizRationale($id: ID!) {
      product(id: $id) {
        metafield(namespace: "custom", key: "quiz_rationale") {
          type
          value
          references(first: 25) {
            edges {
              node {
                ... on Metaobject {
                  handle
                  fields {
                    key
                    value
                  }
                }
              }
            }
          }
        }
      }
    }
  `;

  const data = await storefrontQuery(query, { id: gid });

  const edges = data?.product?.metafield?.references?.edges || [];
  if (!edges.length) {
    return [];
  }

  return edges.map(edge => edge?.node).filter(Boolean);
}

/**************************************
 * RESOLVE FIRST MATCHING RATIONALE METAOBJECT
 **************************************/
// Returns the first candidate handle that yields usable copy, so a CEP primary degrades
// to the nmn-* copy when the cep-* entry is missing OR present but empty. Matching only
// on handle would stop at an empty cep-* metaobject and never reach the nmn-* one.
function resolveQuizRationale(metaobjects, metaobjectHandles) {
  for (const handle of metaobjectHandles) {
    if (!handle) continue;
    const match = metaobjects.find(node => node.handle === handle);
    if (!match || !Array.isArray(match.fields)) continue;
    const rationale = extractRecommendationRationale(match.fields);
    if (rationale) {
      return { handle, rationale };
    }
  }
  return null;
}

/**************************************
 * RENDER RECOMMENDATION RATIONALE (ACCORDION)
 **************************************/
function renderRecommendationRationale(rationale, firstName) {
  if (!rationale || !rationale.description) {
    return '';
  }

  const titleString = firstName ? `${firstName}'s ${rationale.title}` : rationale.title;
  const html = `
    <div class="qp-recommendation-rationale">
      <ul class="accordian js__accordian qp-dynamic-accordian">
        <li class="outer-list">
          <h3 class="qp-rationale-title">
            ${titleString}
            <svg class="qp-accordion-collapsed" xmlns="http://www.w3.org/2000/svg" width="15.117" height="15.117" viewBox="0 0 15.117 15.117">
              <g transform="translate(1 1)">
                <path d="M7027.74,1021v13.117" transform="translate(-7020.74 -1021)" fill="none" stroke="#6b86ab" stroke-linecap="round" stroke-width="2"/>
                <path d="M0,0V13.117" transform="translate(0 6) rotate(-90)" fill="none" stroke="#6b86ab" stroke-linecap="round" stroke-width="2"/>
              </g>
            </svg>
            <svg class="qp-accordion-expanded" style="display:none;" xmlns="http://www.w3.org/2000/svg" width="15.117" height="2" viewBox="0 0 15.117 2">
              <g transform="translate(1 -5)">
                <path d="M0,0V13.117" transform="translate(0 6) rotate(-90)" fill="none" stroke="#6b86ab" stroke-linecap="round" stroke-width="2"/>
              </g>
            </svg>
          </h3>
            <div class="qp-rationale-description">
            ${rationale.description}
            </div>
        </li>
      </ul>
    </div>
  `;
  return html;
}

// The delivery intervals every subscribable product in a variant offers. The lines go into one
// order on one cadence, so an interval only one product carries would put the rest on a schedule
// the shopper never picked. A product with no selling plans at all cannot subscribe on any
// interval, so it sits out of the intersection and rides along as a one-time line.
function getSharedPlans(products) {
  var subscribable = products.filter(function (entry) { return (entry.sellingPlans || []).length > 0; });
  if (subscribable.length === 0) return [];

  return subscribable[0].sellingPlans.filter(function (plan) {
    return subscribable.every(function (entry) {
      return entry.sellingPlans.some(function (candidate) { return candidate.days === plan.days; });
    });
  });
}

// One product's plan on a shared interval. The same lookup backs pricing, the cart lines and the
// picker, so a quoted total and the plan actually added cannot drift apart.
function getPlanOnInterval(entry, sharedPlan) {
  if (!sharedPlan) return null;
  return (entry.sellingPlans || []).find(function (plan) { return plan.days === sharedPlan.days; }) || null;
}

// Totals for a variant on each interval its products share, so the price a shopper sees never
// depends on which control they touched last. Every subscribable product has a plan on each of
// those intervals by construction, and each line is multiplied by its own plan's unit count.
function buildVariantPricing(products, sharedPlans) {
  var pricing = {};

  sharedPlans.forEach(function (sharedPlan) {
    var total = 0;
    var was = 0;

    products.forEach(function (entry) {
      var plan = getPlanOnInterval(entry, sharedPlan);
      var quantity = plan ? plan.quantity || 1 : 1;
      total += plan ? plan.perDeliveryPrice * quantity : entry.variantPrice;
      was += (plan && plan.compareAt ? plan.compareAt : entry.variantPrice) * quantity;
    });

    pricing[sharedPlan.id] = {
      total: total,
      was: was,
      discountPercent: was > total ? Math.round(((was - total) / was) * 100) : 0
    };
  });

  return pricing;
}

// Plan-derived fields for a variant option: the intervals its products share, the totals on each
// of them, and the cart lines seeded to the first shared interval.
function buildOptionPlanFields(products) {
  var sharedPlans = getSharedPlans(products);
  var pricing = buildVariantPricing(products, sharedPlans);
  var firstPlan = sharedPlans[0];
  var active = firstPlan ? pricing[firstPlan.id] : null;
  var listPrice = products.reduce(function (sum, entry) { return sum + entry.variantPrice; }, 0);

  return {
    sellingPlans: sharedPlans,
    pricing: pricing,
    subscriptionPrice: active ? active.total : listPrice,
    discountPercent: active ? active.discountPercent : 0,
    items: products.map(function (entry) {
      var plan = getPlanOnInterval(entry, firstPlan);
      return {
        variantId: entry.product.variants[0].id,
        sellingPlanId: plan ? plan.id : '',
        quantity: plan ? plan.quantity : 1,
        allSellingPlans: entry.sellingPlans
      };
    })
  };
}

// Writes every plan-dependent figure for a variant: the subscription price, the same units at
// list price, and the saving. Reads the plan from the picker so the two controls cannot disagree.
function applyVariantPlanDisplay(container, active) {
  if (!container || !active) return;

  var planSelect = container.querySelector('.qp-plan-select');
  var selectedId = planSelect && planSelect.value ? planSelect.value : (active.sellingPlans || [])[0]?.id;
  var row = (active.pricing && active.pricing[selectedId]) || (active.pricing && active.pricing[(active.sellingPlans || [])[0]?.id]);

  var subPriceStr = '$' + (row ? row.total : active.subscriptionPrice).toFixed(2);
  var wasStr = '$' + (row ? row.was : active.oneTimePrice).toFixed(2);

  container.querySelectorAll('.qp-variant-sub-price').forEach(function (el) { el.textContent = subPriceStr; });
  container.querySelectorAll('.qp-old-price.qp-variant-otp-price').forEach(function (el) { el.textContent = wasStr; });
  // The one-time row is its own basket — always a single unit of each product.
  container.querySelectorAll('.qp-current-price.qp-variant-otp-price').forEach(function (el) {
    el.textContent = '$' + active.oneTimePrice.toFixed(2);
  });

  var save = container.querySelector('.qp-suscription-save');
  if (save) {
    var percent = row ? row.discountPercent : active.discountPercent;
    save.innerHTML = percent ? '<span class="qp-save-extra">Save ' + percent + '%</span>' : '';
  }
}

// Resolves the variant currently on screen and repaints its plan-dependent figures. Falls back
// to the single-product applier when there is no variant host, which is the pair-with card.
function applyActiveVariantDisplay(scopeEl, fallbackPlans) {
  if (!scopeEl) return;

  var variantHost = scopeEl.closest('[data-variant-options]') || scopeEl.querySelector('[data-variant-options]');
  if (!variantHost) {
    var select = scopeEl.querySelector('.qp-plan-select');
    var plans = fallbackPlans || [];
    var chosen = (select && select.value && plans.find(function (p) { return p.id === select.value; })) || plans[0];
    applySelectedPlanDisplay(scopeEl, chosen);
    return;
  }

  try {
    var options = JSON.parse(variantHost.getAttribute('data-variant-options'));
    var activeBtn = variantHost.querySelector('.qp-variant-btn--active');
    var index = activeBtn ? parseInt(activeBtn.getAttribute('data-variant-index'), 10) : 0;
    applyVariantPlanDisplay(variantHost, options[index] || options[0]);
  } catch (err) {}
}

// The picker's options. data-days carries the normalized cadence so a routine switch can keep the
// shopper on the same interval, and data-quantity the unit count the no-JS form action needs.
function renderPlanOptions(plans) {
  return (plans || [])
    .map(function (plan) {
      return '<option value="' + plan.id + '" data-quantity="' + (plan.quantity || 1) +
        '" data-days="' + plan.days + '">' + getPlanLabel(plan) + '</option>';
    })
    .join('');
}

// Repoints the picker at the active routine's shared intervals. Rebuilding is what keeps the
// intersection honest: without it a cadence chosen on one routine survives into another whose
// products cannot all ship it. The chosen interval carries over when the new line-up offers it.
function syncPlanSelect(container, plans) {
  var selected = '';

  container.querySelectorAll('.qp-plan-select').forEach(function (select) {
    var previous = select.selectedOptions[0];
    var previousDays = previous ? previous.getAttribute('data-days') : null;
    var carried = (plans || []).find(function (plan) { return String(plan.days) === previousDays; });

    select.innerHTML = renderPlanOptions(plans);
    select.value = (carried || (plans || [])[0])?.id || '';
    selected = select.value;
  });

  // An empty input means one-time is selected, and that stays the shopper's choice.
  container.querySelectorAll('form.qp-atc-form .sp-input').forEach(function (input) {
    if (input.value) input.value = selected ? selected.split('/').pop() : '';
  });
}

// Products that share no delivery interval cannot ship as one subscription, so that routine is
// one-time only. Dispatching the change lets the existing radio handler clear the plan input and
// rewrite the no-JS checkout URL rather than duplicating that here.
function syncPurchaseAvailability(container, plans) {
  var canSubscribe = (plans || []).length > 0;

  container.querySelectorAll('.js__qp-subscribe, .js__qp-subscribe-save').forEach(function (el) {
    el.style.display = canSubscribe ? '' : 'none';
  });

  if (canSubscribe) return;

  container.querySelectorAll('.js__qp-onetime input[type="radio"]').forEach(function (radio) {
    if (radio.checked) return;
    radio.checked = true;
    radio.dispatchEvent(new Event('change', { bubbles: true }));
  });
}

/**************************************
 * BUILD VARIANT OPTIONS FROM PRODUCTS DATA
 **************************************/
function buildVariantOptions(validProducts, showCepAssets) {
  if (!validProducts || validProducts.length === 0) return [];

  const first = validProducts[0];
  const options = [];

  const singleOption = Object.assign(
    {
      key: 'single',
      label: first.product.title,
      image: first.product.image,
      productTitle: first.product.title,
      oneTimePrice: first.variantPrice
    },
    buildOptionPlanFields([first])
  );

  if (validProducts.length > 1) {
    const second = validProducts[1];

    // Combined hero image lives on the SECONDARY product, which carries both combo shots:
    // nmn_image is "NMN + X", cep_image is "CEP + X". Only reach for cep_image when CEP is
    // actually the product being recommended; otherwise fall back to the NMN shot.
    const nmnCombo = second.metafields?.nmnImage;
    const cepCombo = second.metafields?.cepImage;
    const useCepCombo = showCepAssets && !!(cepCombo && cepCombo.url);
    const comboMeta = useCepCombo ? cepCombo : nmnCombo;
    const combinedImage = (comboMeta && comboMeta.url) || first.product.image;

    options.push(
      Object.assign(
        {
          key: 'combined',
          label: first.product.title + ' + <span class="second-product-name">' + second.product.title + '</span>',
          image: combinedImage,
          productTitle: first.product.title + ' + ' + second.product.title,
          oneTimePrice: first.variantPrice + second.variantPrice
        },
        buildOptionPlanFields([first, second])
      )
    );
  }

  options.push(singleOption);

  return options;
}

/**************************************
 * RENDER "WHY THESE PRODUCTS FIT YOUR NEEDS"
 **************************************/
function renderWhyProductsFit(anchorId, rationaleDescription, productLabelPrimary, productLabelSecondary, showSecondary) {
  var content =
    rationaleDescription ||
    '<p>Based on your quiz responses, this combination has been specifically selected to address your unique health profile.</p>';

  var primaryLabelHtml = productLabelPrimary
    ? `<img src="${productLabelPrimary.url}" alt="${productLabelPrimary.altText || 'Product Label'}" class="qp-product-label qp-product-label--primary" loading="lazy" />`
    : '';

  var secondaryDisplay = showSecondary ? 'display:block;' : 'display:none;';
  var secondaryLabelHtml = productLabelSecondary
    ? `<img src="${productLabelSecondary.url}" alt="${productLabelSecondary.altText || 'Product Label'}" class="qp-product-label qp-product-label--secondary" style="${secondaryDisplay}" loading="lazy" />`
    : '';

  return `
    <div class="qp-why-products" id="${anchorId || ''}">
      <h4 class="qp-why-products__title">Why these products fit your needs</h4>
      <div class="qp-why-products__content">
        ${content}
      </div>
      ${primaryLabelHtml}
      ${secondaryLabelHtml}
    </div>
  `;
}

/**************************************
 * INJECT VARIANT SELECTOR + WHY SECTION STYLES
 **************************************/
function injectVariantSelectorStyles() {
  if (document.getElementById('qp-variant-styles')) return;
  var style = document.createElement('style');
  style.id = 'qp-variant-styles';
  style.textContent = `
    .qp-variant-selector {
      display: flex;
      gap: 8px;
      margin: 12px 0 4px;
    }
    .qp-variant-btn {
      flex: 1;
      padding: 10px 16px;
      border: 1.5px solid #d4d4d4;
      border-radius: 8px;
      background: #fff;
      cursor: pointer;
      font-size: 14px;
      font-weight: 500;
      color: #333;
      transition: border-color 0.2s, background 0.2s, color 0.2s;
      text-align: center;
      line-height: 1.3;
    }
    .qp-variant-btn:hover {
      border-color: #999;
    }
    .qp-variant-btn--active {
      border-color: #1a1b1b;
      background: #1a1b1b;
      color: #fff;
    }
    .qp-why-products {
      margin-top: 20px;
      padding: 16px 0;
      border-top: 1px solid #e8e8e8;
    }
    .qp-why-products__title {
      font-size: 16px;
      font-weight: 600;
      margin-bottom: 8px;
      color: #1a1b1b;
    }
    .qp-why-products__content p {
      font-size: 14px;
      line-height: 1.6;
      color: #555;
      margin-bottom: 8px;
    }
    .qp-why-products__content p:last-child {
      margin-bottom: 0;
    }
    .qp-routine-label {
      font-size: 16px;
      font-weight: 600;
      color: #1a1b1b;
      margin: 16px 0 8px;
    }
    .qp-why-anchor {
      display: inline-block;
      margin-top: 4px;
      font-size: 14px;
      color: #1a1b1b;
      text-decoration: underline;
      text-underline-offset: 3px;
      cursor: pointer;
      transition: opacity 0.2s;
    }
    .qp-why-anchor:hover {
      opacity: 0.7;
    }
    html {
      scroll-behavior: smooth;
    }
  `;
  document.head.appendChild(style);
}

/**************************************
 * BUILD CHECKOUT URL WITH DISCOUNT
 **************************************/
function buildCheckoutUrl(variantId, sellingPlanId, discount, quantity) {
  var params = new URLSearchParams();
  params.append('id', variantId);
  params.append('quantity', String(quantity || 1));
  if (sellingPlanId) {
    params.append('selling_plan', sellingPlanId);
  }
  if (discount && discount.show && discount.code) {
    params.append('return_to', '/discount/' + discount.code + '?redirect=/checkout');
  } else {
    params.append('return_to', '/checkout');
  }
  return '/cart/add?' + params.toString();
}

/**************************************
 * UPDATE CHECKOUT BUTTON URL
 **************************************/
function updateCheckoutButtonUrl(productEl, productId, discount) {
  var forms = productEl.querySelectorAll('form[data-product-id="' + productId + '"]');
  forms.forEach(function(form) {
    var variantId = form.querySelector('input[name="id"]').value;
    var sellingPlanInput = form.querySelector('.sp-input');
    var sellingPlanId = sellingPlanInput ? sellingPlanInput.value : '';

    // The submit handler intercepts and posts to /cart/add.js, so this URL is the no-JS
    // fallback — it still has to ask for the same units the buy box quoted. Read off the picker
    // rather than the seeded cart lines, which only change when the routine does.
    var planSelect = productEl.querySelector('.qp-plan-select');
    var chosen = sellingPlanId && planSelect ? planSelect.selectedOptions[0] : null;
    var quantity = chosen ? parseInt(chosen.getAttribute('data-quantity'), 10) || 1 : 1;

    var checkoutUrl = buildCheckoutUrl(variantId, sellingPlanId, discount, quantity);
    form.setAttribute('action', checkoutUrl);
  });
}

/**************************************
 * INIT PRODUCT LOGIC
 **************************************/
function initProduct(productEl, productId, sellingPlans, discount) {
  var radios = productEl.querySelectorAll('input[name="purchase_desktop_' + productId + '"], input[name="purchase_mobile_' + productId + '"]');
  
  if (radios.length === 0) {
    return;
  }

  function setSellingPlanOnAllForms(container, value) {
    container.querySelectorAll('form[data-product-id="' + productId + '"]').forEach(function(form) {
      var input = form.querySelector('.sp-input');
      if (input) input.value = value;
    });
  }

  productEl.addEventListener("change", function(e) {
    var target = e.target;

    if (target.type === 'radio' && target.name.indexOf('purchase_') !== -1 && target.name.indexOf('_' + productId) !== -1) {
      var productContainer = target.closest('.quiz-product--desktop, .quiz-product--mobile');
      if (!productContainer) return;

      var planSelect = productContainer.querySelector(".qp-plan-select");
      var subscriptionDetails = productContainer.querySelector(".qp-subscription .qp-details");

      if (target.value === "subscription") {
        if (planSelect) planSelect.style.display = "block";
        if (subscriptionDetails) subscriptionDetails.style.display = "block";
        // Whatever the picker currently reads, not the first plan.
        var currentPlanId = (planSelect && planSelect.value) || sellingPlans[0]?.id;
        if (currentPlanId) {
          setSellingPlanOnAllForms(productContainer, currentPlanId.split("/").pop());
        }
        applyActiveVariantDisplay(productContainer, sellingPlans);
      } else {
        if (planSelect) planSelect.style.display = "none";
        if (subscriptionDetails) subscriptionDetails.style.display = "none";
        setSellingPlanOnAllForms(productContainer, "");
      }

      applyPurchaseTypeDisplay(productContainer, target.value === "subscription");
      updateCheckoutButtonUrl(productContainer, productId, discount);
    }

    if (target.classList.contains('qp-plan-select')) {
      var productContainer = target.closest('.quiz-product--desktop, .quiz-product--mobile');
      if (!productContainer) return;
      if (target.value) {
        setSellingPlanOnAllForms(productContainer, target.value.split("/").pop());
        applyActiveVariantDisplay(productContainer, sellingPlans);
        updateCheckoutButtonUrl(productContainer, productId, discount);
      }
    }
  });
  
  var planSelect = productEl.querySelector(".qp-plan-select");
  var subscriptionDetails = productEl.querySelector(".qp-subscription .qp-details");

  if (sellingPlans.length > 0) {
    if (planSelect) planSelect.style.display = "block";
    if (subscriptionDetails) subscriptionDetails.style.display = "block";
    var firstPlanId = sellingPlans[0]?.id;
    if (firstPlanId) {
      setSellingPlanOnAllForms(productEl, firstPlanId.split("/").pop());
    }
    // Seeded through the variant-aware path: a combined variant's price is the sum of both
    // products, which the single-product applier would overwrite with just the first.
    applyActiveVariantDisplay(productEl, sellingPlans);
  }

  syncPurchaseAvailability(productEl, sellingPlans);

  updateCheckoutButtonUrl(productEl, productId, discount);
}

/**************************************
 * INIT VARIANT SELECTOR
 **************************************/
function initVariantSelector(productEl, discount) {
  productEl.addEventListener('click', function(e) {
    var btn = e.target.closest('.qp-variant-btn');
    if (!btn) return;

    var container = btn.closest('[data-variant-options]');
    if (!container) {
      return;
    }

    var variantOptions;
    try {
      variantOptions = JSON.parse(container.getAttribute('data-variant-options'));
    } catch (err) {
      return;
    }

    var index = parseInt(btn.getAttribute('data-variant-index'), 10);
    var active = variantOptions[index];
    if (!active) return;

    // Update active button styling on ALL selectors in this container
    container.querySelectorAll('.qp-variant-btn').forEach(function(b) {
      b.classList.remove('qp-variant-btn--active');
    });
    container.querySelectorAll('.qp-variant-btn[data-variant-index="' + index + '"]').forEach(function(b) {
      b.classList.add('qp-variant-btn--active');
    });

    // Update featured image
    container.querySelectorAll('.qp-variant-image').forEach(function(img) { img.src = active.image; });

    // Toggle secondary product label (only visible for the combined variant)
    var showSecondaryLabel = active && active.key === 'combined';
    container.querySelectorAll('.qp-product-label--secondary').forEach(function(img) {
      img.style.display = showSecondaryLabel ? 'block' : 'none';
    });

    // Toggle the secondary product's TrustScore widget to match its label.
    container.querySelectorAll('.qp-suppco-widget--secondary').forEach(function(el) {
      el.style.display = showSecondaryLabel ? 'block' : 'none';
    });

    // Update product title
    container.querySelectorAll('.qp-variant-title').forEach(function(el) { el.textContent = active.productTitle; });

    // The picker has to be rebuilt before the prices are read off it: the new routine may not
    // offer the cadence the old one did.
    syncPlanSelect(container, active.sellingPlans);
    syncPurchaseAvailability(container, active.sellingPlans);

    // Prices follow the plan the shopper already picked, not the variant's default — otherwise
    // switching routine silently reverts the card to the first tier while the picker still reads
    // the chosen one.
    applyVariantPlanDisplay(container, active);

    // Update form data
    var itemsJSON = JSON.stringify(active.items);
    container.querySelectorAll('form.qp-atc-form').forEach(function(form) {
      form.setAttribute('data-variant-items', itemsJSON);
      var idInput = form.querySelector('input[name="id"]');
      if (idInput) idInput.value = active.items[0].variantId;
    });

    // Update rationale from the JS property (avoids HTML-in-attribute issues)
    var rationaleMap = container._rationaleMap;
    if (rationaleMap && rationaleMap[index]) {
      var rationale = rationaleMap[index];

      if (rationale.description) {
        container.querySelectorAll('.qp-rationale-description').forEach(function(el) {
          el.innerHTML = rationale.description;
        });
        container.querySelectorAll('.qp-why-products__content').forEach(function(el) {
          el.innerHTML = rationale.description;
        });
      }
      if (rationale.title) {
        container.querySelectorAll('.qp-rationale-title').forEach(function(el) {
          el.childNodes[0].textContent = rationale.title;
        });
      }
    }

    var productId = container.querySelector('form.qp-atc-form')?.getAttribute('data-product-id');
    if (productId) {
      updateCheckoutButtonUrl(container, productId, discount);
    }

  });
}

/**************************************
 * HANDLE FORM SUBMISSIONS
 **************************************/
function handleFormSubmissions() {
  // Copy discount code button
  document.addEventListener('click', function(e) {
    var copyBtn = e.target.closest('.qp-discount-copy-btn');
    if (!copyBtn) return;
    var code = copyBtn.getAttribute('data-copy-code');
    if (!code) return;
    e.preventDefault();
    navigator.clipboard.writeText(code).then(function() {
      var svg = copyBtn.querySelector('svg');
      if (svg) {
        var originalSvg = svg.outerHTML;
        svg.outerHTML = '<svg width="16" height="16" viewBox="0 0 16 16" fill="none" xmlns="http://www.w3.org/2000/svg"><path d="M13 4L6 11L3 8" stroke="#22c55e" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"/></svg>';
        copyBtn.disabled = true;
        setTimeout(function() {
          copyBtn.innerHTML = originalSvg;
          copyBtn.disabled = false;
        }, 1500);
      }
    });
  });

  // Handle accordion clicks for recommendation rationale
  document.addEventListener('click', function(e) {
    var accordionHeader = e.target.closest('.qp-dynamic-accordian .outer-list h3');
    if (accordionHeader) {
      e.preventDefault();
      var li = accordionHeader.closest('.outer-list');
      if (li) {
        var isActive = li.classList.contains('active');
        var container = li.closest('.qp-recommendation-rationale');
        container.classList.toggle('open');
        li.classList.toggle('active');
        if (container) {
          container.style.height = isActive ? '85px' : 'fit-content';
        }
      }
    }
  });

  // Intercept all Express Checkout form submissions — AJAX add to cart then redirect
  document.addEventListener('submit', function(e) {
    if (!e.target.classList.contains('qp-atc-form')) return;

    e.preventDefault();
    e.stopPropagation();
    e.stopImmediatePropagation();

    var form = e.target;
    var submitBtn = form.querySelector('button[type="submit"]');
    if (submitBtn) submitBtn.disabled = true;

    var items = [];
    var itemsAttr = form.getAttribute('data-variant-items');

    if (itemsAttr) {
      try {
        var variantItems = JSON.parse(itemsAttr);
        var sellingPlanInput = form.querySelector('.sp-input');
        var sellingPlanValue = sellingPlanInput ? sellingPlanInput.value : '';

        var selectedPlan = null;
        if (sellingPlanValue && variantItems[0]?.allSellingPlans) {
          selectedPlan = variantItems[0].allSellingPlans.find(function(p) {
            return p.id.split('/').pop() === sellingPlanValue;
          });
        }

        // One-time starts from a single unit of each product, which is what the one-time row
        // quotes. Only a plan raises it, and only to its own unit count.
        items = variantItems.map(function(item, idx) {
          var cartItem = { id: parseInt(item.variantId, 10), quantity: 1 };
          if (!sellingPlanValue) return cartItem;

          if (idx === 0) {
            cartItem.selling_plan = parseInt(sellingPlanValue, 10);
            cartItem.quantity = (selectedPlan ? selectedPlan.quantity : item.quantity) || 1;
            return cartItem;
          }

          // The picker only offers intervals every product shares, so a companion has a plan on
          // the selected cadence. Matched on the same day count the pricing used.
          var matchedPlan = selectedPlan && (item.allSellingPlans || []).find(function(p) {
            return p.days === selectedPlan.days;
          });

          if (matchedPlan) {
            cartItem.selling_plan = parseInt(matchedPlan.id.split('/').pop(), 10);
            cartItem.quantity = matchedPlan.quantity || 1;
          } else if (item.sellingPlanId) {
            cartItem.selling_plan = parseInt(item.sellingPlanId.split('/').pop(), 10);
            cartItem.quantity = item.quantity || 1;
          }
          return cartItem;
        });
      } catch (err) {}
    }

    if (items.length === 0) {
      var formData = new FormData(form);
      var item = { id: parseInt(formData.get('id'), 10), quantity: 1 };
      var sp = formData.get('selling_plan');
      if (sp && sp.trim() !== '') {
        item.selling_plan = parseInt(sp, 10);
        // No variant items to read, so the unit count comes off the chosen plan's option.
        var chosen = form.closest('.qp-product')?.querySelector('.qp-plan-select option[value$="' + sp + '"]');
        if (chosen) item.quantity = parseInt(chosen.getAttribute('data-quantity'), 10) || 1;
      }
      items = [item];
    }

    fetch('/cart/add.js', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ items: items })
    })
    .then(function(res) { return res.json(); })
    .then(function() {
      var discountCode = form.getAttribute('data-discount-code') || '';
      if (discountCode) {
        window.location.href = '/discount/' + discountCode + '?redirect=/checkout';
      } else {
        window.location.href = '/checkout';
      }
    })
    .catch(function(error) {
      alert('Failed to add to cart. Please try again.');
      if (submitBtn) submitBtn.disabled = false;
    });
  }, true);

}

/**************************************
 * RENDER STARS (OPTIMIZED)
 **************************************/
function renderStars(productEl, productId) {
  var starsHTML = '★★★★★';
  var containers = productEl.querySelectorAll('.qp-review-stars[data-product-id="' + productId + '"]');
  containers.forEach(function(container) {
    container.innerHTML = starsHTML;
  });
}

/**************************************
 * BUILD PRODUCT HTML - DESKTOP VERSION
 **************************************/
function buildSuppcoTrustWidget(metafields) {
  if (!window.iwShowSuppcoQuizWidget) return '';
  const slug = metafields && metafields.suppcoSlug;
  const show = metafields && metafields.suppcoShow;
  if (!slug || !(show === 'true' || show === '1' || show === true)) return '';
  return `<a href="https://supp.co/products/${encodeURIComponent(slug)}" class="trust-info-widget">TrustScore</a>`;
}

function buildProductHTMLDesktop(activeVariant, primaryProductId, reviews, metafields, hasMultipleVariants, variantOptions, firstName, discount, isPageQuiz) {
  var subscriptionPrice = '$' + activeVariant.subscriptionPrice.toFixed(2);
  var oneTimePrice = '$' + activeVariant.oneTimePrice.toFixed(2);
  var titleText = firstName ? 'Designed for you, ' + firstName + '...' : 'Designed for you...';
  var sellingPlanOptions = renderPlanOptions(activeVariant.sellingPlans);
  var discountPercent = activeVariant.discountPercent;
  var itemsJSON = JSON.stringify(activeVariant.items).replace(/'/g, '&#39;');
  var initialCheckoutUrl = buildCheckoutUrl(activeVariant.items[0].variantId, '', discount);

  return `
    ${renderRecommendationRationale(metafields.recommendationRationale, firstName)}
    <div class="qp-left">
      <img src="${activeVariant.image}" class="qp-image qp-variant-image" loading="lazy" />
      ${metafields.suppcoWidgets || ''}
    </div>
    <div class="qp-right">
      <div class="qp-product-header">
        <h2 class="qp-title">${titleText}</h2>
        <a href="#qp-why-products-desktop" class="qp-why-anchor">Why these products fit your needs</a>
        <div class="qp-reviews-wrapper">
          <div class="qp-review-stars" data-product-id="${primaryProductId}"></div>
          <div class="qp-review-count">${formatNumberWithCommas(reviews.count)} Reviews</div>
        </div>
        <h3 class="qp-product-title qp-variant-title">${activeVariant.productTitle}</h3>
        <div class="qp-price-info--first">
          <h3 class="qp-product-price qp-variant-sub-price">${subscriptionPrice}</h3>
          <span class="qp-old-price qp-variant-otp-price">${oneTimePrice}</span>
        </div>
        ${discount && discount.show ? `<div class="qp-product-discount"><span>${discount.percent}% OFF</span><span class="qp-discount-code">Code: ${discount.code}</span>${isPageQuiz ? `<button type="button" class="qp-discount-copy-btn" data-copy-code="${discount.code}" title="Copy code"><svg viewBox="0 0 1024 1024" xmlns="http://www.w3.org/2000/svg" fill="#000000" transform="rotate(270)"><g id="SVGRepo_bgCarrier" stroke-width="0"></g><g id="SVGRepo_tracerCarrier" stroke-linecap="round" stroke-linejoin="round"></g><g id="SVGRepo_iconCarrier"><path fill="#000000" d="M768 832a128 128 0 0 1-128 128H192A128 128 0 0 1 64 832V384a128 128 0 0 1 128-128v64a64 64 0 0 0-64 64v448a64 64 0 0 0 64 64h448a64 64 0 0 0 64-64h64z"></path><path fill="#000000" d="M384 128a64 64 0 0 0-64 64v448a64 64 0 0 0 64 64h448a64 64 0 0 0 64-64V192a64 64 0 0 0-64-64H384zm0-64h448a128 128 0 0 1 128 128v448a128 128 0 0 1-128 128H384a128 128 0 0 1-128-128V192A128 128 0 0 1 384 64z"></path></g></svg></button>` : ''}</div>` : ''}
        ${hasMultipleVariants ? `
        <h4 class="qp-routine-label">Choose your ideal Routine:</h4>
        <div class="qp-variant-selector">
          ${variantOptions.map(function(opt, i) { return '<button type="button" class="qp-variant-btn' + (i === 0 ? ' qp-variant-btn--active' : '') + '" data-variant-index="' + i + '">' + opt.label + '</button>'; }).join('')}
        </div>
        ` : ''}
      </div>
      <div class="qp-subscription-container">
        <div class="qp-suscription-save js__qp-subscribe-save">${discountPercent ? `<span class="qp-save-extra">Save Extra ${discountPercent}%</span>` : ""}</div>
        <div class="qp-subscription js__qp-subscribe">
          <label>
            <div class="qp-radio">
              <input type="radio" name="purchase_desktop_${primaryProductId}" value="subscription" checked/>
              <span class="qp-radio__label">Subscribe & Save</span>
            </div>
            <div class="qp-price-info">
              <span class="qp-current-price qp-variant-sub-price">${subscriptionPrice}</span>
              <span class="qp-old-price qp-variant-otp-price">${oneTimePrice}</span>
            </div>
          </label>
          <select class="qp-plan-select" data-product-id="${primaryProductId}" style="display:none;">${sellingPlanOptions}</select>
          <div class="qp-details" style="display:none;">
            <div class="qp-detail">
              <svg width="16" height="16" viewBox="0 0 16 16" fill="none" xmlns="http://www.w3.org/2000/svg">
                <path fill-rule="evenodd" clip-rule="evenodd" d="M6.66669 10.6L3.13336 7.06662L2.40002 7.73328L5.93336 11.2666L6.66669 12L13.7334 4.93328L13 4.19995L6.66669 10.6Z" fill="#84827E"/>
              </svg>
              Pause or cancel anytime
            </div>
            <div class="qp-detail">
              <svg width="16" height="16" viewBox="0 0 16 16" fill="none" xmlns="http://www.w3.org/2000/svg">
                <path fill-rule="evenodd" clip-rule="evenodd" d="M6.66669 10.6L3.13336 7.06662L2.40002 7.73328L5.93336 11.2666L6.66669 12L13.7334 4.93328L13 4.19995L6.66669 10.6Z" fill="#84827E"/>
              </svg>
              30-day money-back guarantee
            </div>
          </div>
        </div>
        <div class="qp-one-time js__qp-onetime">
          <label>
            <div class="qp-radio">
              <input type="radio" name="purchase_desktop_${primaryProductId}" value="onetime" />
              <span class="qp-radio__label">One-time</span>
            </div>
            <div class="qp-price-info">
              <span class="qp-current-price qp-variant-otp-price">${oneTimePrice}</span>
            </div>
          </label>
        </div>
      </div>
      <div class="qp-product-footer">
        <form class="qp-atc-form" action="${initialCheckoutUrl}" method="post" data-product-id="${primaryProductId}" data-variant-items='${itemsJSON}'${discount && discount.show && discount.code ? ` data-discount-code="${discount.code}"` : ''}>
          <input type="hidden" name="id" value="${activeVariant.items[0].variantId}">
          <input type="hidden" name="selling_plan" class="sp-input" value="" />
          <button type="submit" class="qp-atc-btn btn--primary">
            <span>Express Checkout</span>
            <svg width="24" height="24" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
              <path d="M14.0391 5.08594L12.9609 6.16406L18.0469 11.25H3V12.75H18.0469L12.9609 17.8359L14.0391 18.9141L20.4141 12.5391L20.9297 12L20.4141 11.4609L14.0391 5.08594Z" fill="CurrentColor"/>
            </svg>
          </button>
        </form>
        <div class="qp-product-details">
          <div class="qp-product-details__item">
            <svg width="16" height="16" viewBox="0 0 16 16" fill="none" xmlns="http://www.w3.org/2000/svg">
              <path d="M8 2C4.69141 2 2 4.69141 2 8C2 11.3086 4.69141 14 8 14C11.3086 14 14 11.3086 14 8C14 4.69141 11.3086 2 8 2ZM8 3C10.7676 3 13 5.23242 13 8C13 10.7676 10.7676 13 8 13C5.23242 13 3 10.7676 3 8C3 5.23242 5.23242 3 8 3ZM7.5 4V8.5H11V7.5H8.5V4H7.5Z" fill="#1A1B1B"/>
            </svg>
            <span>Free Shipping (3-4 days)</span>
          </div>
          <div class="qp-product-details__item">
            <svg xmlns="http://www.w3.org/2000/svg" width="16" height="16" viewBox="0 0 16 16" fill="none">
              <path fill-rule="evenodd" clip-rule="evenodd" d="M6.66672 10.6L3.13339 7.06662L2.40005 7.73328L5.93339 11.2666L6.66672 12L13.7334 4.93328L13.0001 4.19995L6.66672 10.6Z" fill="#1A1B1B"/>
            </svg>
            <span>30-Day Risk Free Guarantee</span>
          </div>
        </div>
      </div>
      ${renderWhyProductsFit(
        'qp-why-products-desktop',
        metafields.recommendationRationale?.description,
        metafields.productLabel,
        metafields.secondaryProductLabel,
        activeVariant && activeVariant.key === 'combined'
      )}
    </div>
  `;
}

/**************************************
 * BUILD PRODUCT HTML - MOBILE VERSION
 **************************************/
function buildProductHTMLMobile(activeVariant, primaryProductId, reviews, metafields, hasMultipleVariants, variantOptions, firstName, discount, isPageQuiz) {
  var subscriptionPrice = '$' + activeVariant.subscriptionPrice.toFixed(2);
  var oneTimePrice = '$' + activeVariant.oneTimePrice.toFixed(2);
  var titleText = firstName ? 'Designed for you, ' + firstName + '...' : 'Designed for You...';
  var sellingPlanOptions = renderPlanOptions(activeVariant.sellingPlans);
  var discountPercent = activeVariant.discountPercent;
  var itemsJSON = JSON.stringify(activeVariant.items).replace(/'/g, '&#39;');
  var initialCheckoutUrl = buildCheckoutUrl(activeVariant.items[0].variantId, '', discount);

  return `
    ${renderRecommendationRationale(metafields.recommendationRationale, firstName)}
    <h2 class="qp-title qp-title--mobile">${titleText}</h2>
    <a href="#qp-why-products-mobile" class="qp-why-anchor">Why these products fit your needs</a>
    <div class="qp-mobile-top-row">
      <div class="qp-left qp-left--mobile">
        <img src="${activeVariant.image}" class="qp-image qp-variant-image" loading="lazy" />
      </div>
      <div class="qp-product-header qp-product-header--mobile">
        <div class="qp-reviews-wrapper">
          <div class="qp-review-stars" data-product-id="${primaryProductId}"></div>
          <div class="qp-review-count">${formatNumberWithCommas(reviews.count)} Reviews</div>
        </div>
        <h3 class="qp-product-title qp-variant-title">${activeVariant.productTitle}</h3>
        <div class="qp-price-info--first">
          <h3 class="qp-product-price qp-variant-sub-price">${subscriptionPrice}</h3>
          <span class="qp-old-price qp-variant-otp-price">${oneTimePrice}</span>
        </div>
        ${discount && discount.show ? `<div class="qp-product-discount"><span>${discount.percent}% OFF</span><span class="qp-discount-code">Code: ${discount.code}</span>${isPageQuiz ? `<button type="button" class="qp-discount-copy-btn" data-copy-code="${discount.code}" title="Copy code"><svg viewBox="0 0 1024 1024" xmlns="http://www.w3.org/2000/svg" fill="#000000" transform="rotate(270)"><g id="SVGRepo_bgCarrier" stroke-width="0"></g><g id="SVGRepo_tracerCarrier" stroke-linecap="round" stroke-linejoin="round"></g><g id="SVGRepo_iconCarrier"><path fill="#000000" d="M768 832a128 128 0 0 1-128 128H192A128 128 0 0 1 64 832V384a128 128 0 0 1 128-128v64a64 64 0 0 0-64 64v448a64 64 0 0 0 64 64h448a64 64 0 0 0 64-64h64z"></path><path fill="#000000" d="M384 128a64 64 0 0 0-64 64v448a64 64 0 0 0 64 64h448a64 64 0 0 0 64-64V192a64 64 0 0 0-64-64H384zm0-64h448a128 128 0 0 1 128 128v448a128 128 0 0 1-128 128H384a128 128 0 0 1-128-128V192A128 128 0 0 1 384 64z"></path></g></svg></button>` : ''}</div>` : ''}
      </div>
    </div>
    <div class="qp-mobile-full-width">
      ${hasMultipleVariants ? `
      <h4 class="qp-routine-label">Choose your ideal Routine:</h4>
      <div class="qp-variant-selector">
        ${variantOptions.map(function(opt, i) { return '<button type="button" class="qp-variant-btn' + (i === 0 ? ' qp-variant-btn--active' : '') + '" data-variant-index="' + i + '">' + opt.label + '</button>'; }).join('')}
      </div>
      ` : ''}
      <div class="qp-subscription-container">
        <div class="qp-suscription-save js__qp-subscribe-save">${discountPercent ? `<span class="qp-save-extra">Save extra ${discountPercent}%</span>` : ""}</div>
        <div class="qp-subscription js__qp-subscribe">
          <label>
            <div class="qp-radio">
              <input type="radio" name="purchase_mobile_${primaryProductId}" value="subscription" checked/>
              <span class="qp-radio__label">Subscribe & Save</span>
            </div>
            <div class="qp-price-info">
              <span class="qp-current-price qp-variant-sub-price">${subscriptionPrice}</span>
              <span class="qp-old-price qp-variant-otp-price">${oneTimePrice}</span>
            </div>
          </label>
          <select class="qp-plan-select" data-product-id="${primaryProductId}" style="display:none;">${sellingPlanOptions}</select>
          <div class="qp-details" style="display:none;">
            <div class="qp-detail">
              <svg width="16" height="16" viewBox="0 0 16 16" fill="none" xmlns="http://www.w3.org/2000/svg">
                <path fill-rule="evenodd" clip-rule="evenodd" d="M6.66669 10.6L3.13336 7.06662L2.40002 7.73328L5.93336 11.2666L6.66669 12L13.7334 4.93328L13 4.19995L6.66669 10.6Z" fill="#84827E"/>
              </svg>
              Delivery every 30 days
            </div>
            <div class="qp-detail">
              <svg width="16" height="16" viewBox="0 0 16 16" fill="none" xmlns="http://www.w3.org/2000/svg">
                <path fill-rule="evenodd" clip-rule="evenodd" d="M6.66669 10.6L3.13336 7.06662L2.40002 7.73328L5.93336 11.2666L6.66669 12L13.7334 4.93328L13 4.19995L6.66669 10.6Z" fill="#84827E"/>
              </svg>
              Pause or cancel anytime
            </div>
          </div>
        </div>
        <div class="qp-one-time js__qp-onetime">
          <label>
            <div class="qp-radio">
              <input type="radio" name="purchase_mobile_${primaryProductId}" value="onetime"/>
              <span class="qp-radio__label">One-time</span>
            </div>
            <div class="qp-price-info">
              <span class="qp-current-price qp-variant-otp-price">${oneTimePrice}</span>
            </div>
          </label>
        </div>
      </div>
      ${metafields.suppcoWidgets || ''}
      <div class="qp-product-footer">
        <form class="qp-atc-form" action="${initialCheckoutUrl}" method="post" data-product-id="${primaryProductId}" data-variant-items='${itemsJSON}'${discount && discount.show && discount.code ? ` data-discount-code="${discount.code}"` : ''}>
          <input type="hidden" name="id" value="${activeVariant.items[0].variantId}">
          <input type="hidden" name="selling_plan" class="sp-input" value="" />
          <button type="submit" class="qp-atc-btn btn--primary">
            <span>Express Checkout</span>
            <svg width="24" height="24" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
              <path d="M14.0391 5.08594L12.9609 6.16406L18.0469 11.25H3V12.75H18.0469L12.9609 17.8359L14.0391 18.9141L20.4141 12.5391L20.9297 12L20.4141 11.4609L14.0391 5.08594Z" fill="CurrentColor"/>
            </svg>
          </button>
        </form>
        <div class="qp-product-details">
          <div class="qp-product-details__item">
            <svg width="16" height="16" viewBox="0 0 16 16" fill="none" xmlns="http://www.w3.org/2000/svg">
              <path d="M8 2C4.69141 2 2 4.69141 2 8C2 11.3086 4.69141 14 8 14C11.3086 14 14 11.3086 14 8C14 4.69141 11.3086 2 8 2ZM8 3C10.7676 3 13 5.23242 13 8C13 10.7676 10.7676 13 8 13C5.23242 13 3 10.7676 3 8C3 5.23242 5.23242 3 8 3ZM7.5 4V8.5H11V7.5H8.5V4H7.5Z" fill="#1A1B1B"/>
            </svg>
            <span>Free Shipping (3-4 days)</span>
          </div>
          <div class="qp-product-details__item">
            <svg xmlns="http://www.w3.org/2000/svg" width="16" height="16" viewBox="0 0 16 16" fill="none">
              <path fill-rule="evenodd" clip-rule="evenodd" d="M6.66672 10.6L3.13339 7.06662L2.40005 7.73328L5.93339 11.2666L6.66672 12L13.7334 4.93328L13.0001 4.19995L6.66672 10.6Z" fill="#1A1B1B"/>
            </svg>
            <span>30-Day Risk Free Guarantee</span>
          </div>
        </div>
      </div>
      ${renderWhyProductsFit(
        'qp-why-products-mobile',
        metafields.recommendationRationale?.description,
        metafields.productLabel,
        metafields.secondaryProductLabel,
        activeVariant && activeVariant.key === 'combined'
      )}
    </div>
  `;
}

// Get dynamic content from the bottom_content field. Returns null unless the payload
// carries both entries — the caller already treats a falsy result as nothing to render.
function getDynamicContent(dynamicContent) {
  const title = dynamicContent?.[0]?.text;
  const body = dynamicContent?.[1]?.text;
  if (!title || !body) return null;

  return `<div class="outcome_buckets_content">
  <div class="outcome_bucket">
    <div class="outcome_bucket_title">
      <h1>${title}</h1>
    </div>
    <div class="outcome_bucket_content">
      <p><strong>${body}</strong></p>
    </div>
  </div>
  
  </div>`;
}

/**************************************
 * MAIN QUIZ EXECUTION
 **************************************/
document.addEventListener("octane.quiz.completed", async function (e) {
  const eventData = e.detail;
  const quizId = eventData.quiz_id;
  const pageQuizId = getPageQuizId();
  const isPageQuiz = quizId === pageQuizId;
  const answer = eventData.answers["Octane: What other areas are you concerned about?"];

  let results;
  let email = '';
  let phone = '';
  
  if (eventData.state && eventData.state.headless_results_page_accessed) {
    results = eventData.state.headless_results_page_accessed;
    results.info = eventData.state.info;
    email = eventData.state.email || results.email || eventData.email || '';
    phone = eventData.state.phone || results.phone || eventData.phone || '';
  } else if (eventData.sections) {
    results = eventData;
    email = results.email || '';
    phone = results.phone || '';
  } else {
    return;
  }
  
  const container = await waitForResultsContainer();
  
  const firstName = extractFirstName(results.answers);
  const discount = calculateDiscount(email, phone);
  // CEP competes with NMN as the primary product. Octane shows CEP when
  // accelerated_cellular >= 1 (NMN when = 0); mirror that to pick the combo image.
  const isCepPrimary = getScore(results.answers, 'accelerated_cellular') >= 1;
  
  const fragment = document.createDocumentFragment();

  const hasProducts = results.sections && 
                      results.sections.length > 0 && 
                      results.sections.some(section => 
                        section.products && 
                        Array.isArray(section.products) && 
                        section.products.length > 0
                      );
  
  if (!hasProducts) {
    return;
  }

  for (const section of results.sections) {
    const sectionWrapper = document.createElement("div");
    sectionWrapper.className = "quiz-section";

    if (isPageQuiz) {
      const dynamicContent = getDynamicContent(eventData.sections?.[0]?.['bottom_content']);
      if (dynamicContent) {
        sectionWrapper.insertAdjacentHTML('beforeend', dynamicContent);
      }
    }

    const productsData = await Promise.all(
      section.products.map(async (product) => {
        const handle = getHandleFromUrl(product.link);
        
        if (!handle) {
          return null;
        }

        const data = await fetchAllProductData(product.id, handle);
        return { product, ...data };
      })
    );

    const validProducts = productsData.filter(p => p !== null);

    if (validProducts.length > 0) {
      const firstProductData = validProducts[0];
      const primaryProductId = firstProductData.product.id;

      let combinedRationale = null;
      let singleRationale = null;
      let primaryIsCep = false;

      // Primary product drives the metaobject handle prefix: CEP uses cep-* (cep-cardioprime,
      // cep-only, ...), NMN uses nmn-*. The matching metaobjects are referenced from the
      // primary product's custom.quiz_rationale metafield. A CEP primary retries the nmn-*
      // handle on a miss, degrading to the NMN copy rather than the generic rationale.
      // Runs for every quiz: a product without quiz_rationale entries simply resolves
      // nothing and drops through to the recommendation_rationale fallback below.
      const rationalePrefixes = isCepPrimary ? ['cep', 'nmn'] : ['nmn'];
      try {
        const rationaleMetaobjects = await fetchQuizRationaleMetaobjects(firstProductData.product.id);
        primaryIsCep = rationaleMetaobjects.some(node => typeof node.handle === 'string' && node.handle.startsWith('cep-'));

        // Combined rationale: from quiz_rationale matched by quiz answer
        const combinedHandles = rationalePrefixes.map(prefix => getQuizRationaleHandleFromAnswer(answer, prefix));
        const combinedMatch = resolveQuizRationale(rationaleMetaobjects, combinedHandles);
        if (combinedMatch) {
          combinedRationale = combinedMatch.rationale;
        }

        // Single-product rationale: handle "<prefix>-only" (nmn-only / cep-only)
        const onlyHandles = rationalePrefixes.map(prefix => prefix + '-only');
        const onlyMatch = resolveQuizRationale(rationaleMetaobjects, onlyHandles);
        if (onlyMatch) {
          singleRationale = onlyMatch.rationale;
        }
      } catch (err) {}

      // Fallbacks
      if (!singleRationale) {
        singleRationale = extractRecommendationRationale(firstProductData.metafields?.productRationale);
      }
      if (!combinedRationale) {
        combinedRationale = singleRationale;
      }

      // Use combined as default for initial render
      const recommendationRationale = combinedRationale;

      // CEP assets only surface when the primary product actually carries CEP content.
      // The score can reach 1 before Octane's product matching swaps CEP into the results,
      // and the secondary product holds both combo shots, so the score alone would put a
      // "CEP + X" image on an NMN recommendation.
      const showCepAssets = isCepPrimary && primaryIsCep;

      // Build variant options from ALL products
      const variantOptions = buildVariantOptions(validProducts, showCepAssets);

      // Build rationale map separately (HTML can't safely go in data attributes)
      var rationaleMap = {};
      variantOptions.forEach(function(opt, idx) {
        if (opt.key === 'combined' && combinedRationale) {
          rationaleMap[idx] = { title: combinedRationale.title || '', description: combinedRationale.description || '' };
        } else if (opt.key === 'single' && singleRationale) {
          rationaleMap[idx] = { title: singleRationale.title || '', description: singleRationale.description || '' };
        } else {
          rationaleMap[idx] = { title: '', description: '' };
        }
      });

      const activeVariant = variantOptions[0];
      const hasMultipleVariants = variantOptions.length > 1;

      const secondaryProductLabel = validProducts.length > 1 ? validProducts[1].metafields?.productLabel : null;

      const metafields = {
        ...firstProductData.metafields,
        secondaryProductLabel,
        recommendationRationale,
        // One TrustScore widget per recommended product (both stacked together).
        // The second product's widget mirrors its product label: only shown for
        // the 'combined' variant (hidden when a single-product routine is picked).
        suppcoWidgets: validProducts.map(function (p, i) {
          var widget = buildSuppcoTrustWidget(p.metafields);
          if (!widget) return '';
          return i === 0 ? widget : '<div class="qp-suppco-widget--secondary">' + widget + '</div>';
        }).join('')
      };

      // Desktop version
      const productElDesktop = document.createElement("div");
      productElDesktop.className = "quiz-product quiz-product--desktop";
      productElDesktop.innerHTML = buildProductHTMLDesktop(
        activeVariant, primaryProductId, firstProductData.reviews, metafields,
        hasMultipleVariants, variantOptions, firstName, discount, isPageQuiz
      );
     
      if (hasMultipleVariants) {
        productElDesktop.setAttribute('data-variant-options', JSON.stringify(variantOptions));
        productElDesktop._rationaleMap = rationaleMap;
      }

      sectionWrapper.appendChild(productElDesktop);
      initProduct(productElDesktop, primaryProductId, activeVariant.sellingPlans, discount);
      initVariantSelector(productElDesktop, discount);
      renderStars(productElDesktop, primaryProductId);

      // Mobile version
      const productElMobile = document.createElement("div");
      productElMobile.className = "quiz-product quiz-product--mobile";
      productElMobile.innerHTML = buildProductHTMLMobile(
        activeVariant, primaryProductId, firstProductData.reviews, metafields,
        hasMultipleVariants, variantOptions, firstName, discount, isPageQuiz
      );
      if (hasMultipleVariants) {
        productElMobile.setAttribute('data-variant-options', JSON.stringify(variantOptions));
        productElMobile._rationaleMap = rationaleMap;
      }

      sectionWrapper.appendChild(productElMobile);
      initProduct(productElMobile, primaryProductId, activeVariant.sellingPlans, discount);
      initVariantSelector(productElMobile, discount);
      renderStars(productElMobile, primaryProductId);

      fragment.appendChild(sectionWrapper);
       setTimeout(function(){ 


        const trigger = document.querySelector('.qp-why-anchor');
        const target = document.querySelector('.qp-why-products');


        trigger.addEventListener('click', function (e) {
          e.preventDefault();

          const headerOffset = document.querySelector('header')?.offsetHeight || 80; // fallback
          const elementPosition = target.getBoundingClientRect().top + window.pageYOffset;
          const offsetPosition = elementPosition - headerOffset - 10; // extra spacing if needed

          window.scrollTo({
            top: offsetPosition,
            behavior: 'smooth'
          });
        });
        }, 1000);
    }
  }
  
  const existingHeader = container.querySelector('.custom-quiz-header');
  const hasRetakeButton = existingHeader?.querySelector('#custom-retake-button');

  container.innerHTML = "";

  if (existingHeader && hasRetakeButton) {
    container.appendChild(existingHeader);
  }

  container.appendChild(fragment);

  if (!existingHeader || !hasRetakeButton) {
    if (typeof window.createCustomHeader === 'function') {
      window.createCustomHeader(container, true);
    }
  }
});

// Initialize recommendation rationale accordions
function initializeRecommendationRationales() {
  var accordions = document.querySelectorAll('.qp-recommendation-rationale .qp-dynamic-accordian .outer-list');
  accordions.forEach(function(li) {
    li.classList.remove('active');
  });
}

// Call when DOM is ready
if (document.readyState === 'loading') {
  document.addEventListener('DOMContentLoaded', function() {
    injectVariantSelectorStyles();
    handleFormSubmissions();
    setTimeout(initializeRecommendationRationales, 100);
  });
} else {
  injectVariantSelectorStyles();
  handleFormSubmissions();
  setTimeout(initializeRecommendationRationales, 100);
}

